import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SideNavLayout from "../customsidenav/sidenavlayout";
import { showToast } from "../reduxtoolkit/toastslice";
import { practiceShortName } from "../config/practice";
import { getAccess, getSummary, interpError } from "./interpReviewApi";
import CalibrationTab from "./CalibrationTab";
import EchoTab from "./EchoTab";
import AdminTab from "./AdminTab";
import { Card } from "./ui";

/*
 * Interpretation Review (/interp-review, owner request 10-26, C-HIM 9928 only): Dr. Madhu's blinded A/B
 * calibration of ultrasound reads (AI draft vs Experity final) and his review of AI echo draft interpretations.
 * A study / review tool -- nothing is signed or filed to Athena or the chart. The route and the nav item need the
 * "Interpretation Review" Side Bar privilege; the server checks it (and the practice) on every call.
 *
 * Deep links: /interp-review?tab=calibration|echo|admin&item=<id> (the Tasks page links here).
 */

const TABS = [["calibration", "Blind calibration"], ["echo", "Echo drafts"], ["admin", "Admin"]];

const InterpReview = () => {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const wantTab = params.get("tab");
  const initialItem = Number(params.get("item")) || null;
  const [tab, setTab] = useState(TABS.some(([k]) => k === wantTab) ? wantTab : "calibration");
  const [access, setAccess] = useState(null);
  // Calibration cases loaded for this reviewer (owner 10-26: calibration skipped, echo-only loader -> 0, tab hidden).
  const [calibrationTotal, setCalibrationTotal] = useState(null);
  const [error, setError] = useState(null);

  const toast = useCallback((message, severity = "info") => dispatch(showToast({ message, severity })), [dispatch]);

  useEffect(() => {
    document.title = `${practiceShortName()} - Interpretation Review`;
    let live = true;
    getAccess()
      .then(async (r) => {
        let total = 0;
        if (r?.allowed) {
          try {
            total = Number((await getSummary())?.calibration?.total) || 0;
          } catch (e) {
            total = 0;
          }
        }
        if (!live) return;
        setCalibrationTotal(total);
        if (!total) setTab((t) => (t === "calibration" ? "echo" : t));
        setAccess(r);
      })
      .catch((e) => live && setError(interpError(e, "Could not open Interpretation Review")));
    return () => {
      live = false;
    };
  }, []);

  const pickTab = (k) => {
    setTab(k);
    setParams({ tab: k }, { replace: true });
  };

  // Keep ?item= in the URL so a reload resumes on the same case / study.
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onItemChange = useCallback((id) => {
    const cur = paramsRef.current;
    if (cur.get("item") === String(id)) return;
    const next = new URLSearchParams(cur);
    next.set("item", String(id));
    setParams(next, { replace: true });
  }, [setParams]);

  const tabs = TABS.filter(([k]) => (k !== "admin" || access?.admin) && (k !== "calibration" || calibrationTotal > 0));
  const itemFor = (k) => (k === (wantTab || "calibration") ? initialItem : null);

  let body;
  if (error) body = <Card className="p-6 text-[13px] text-red-700">{error}</Card>;
  else if (!access) body = <div className="flex justify-center py-16"><CircularProgress size={28} /></div>;
  else if (!access.allowed) {
    body = (
      <Card className="p-6 text-[13px] text-[#374151]">
        {access.practice_enabled ? "This sign-in doesn't have the Interpretation Review privilege."
          : "Interpretation Review is not available in this practice."}
      </Card>
    );
  } else {
    body = (
      <>
        <div className="flex gap-1 border-b border-[#E5E7EB] mb-4" role="tablist">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => pickTab(k)}
              className={`h-11 px-4 text-sm font-medium border-b-2 -mb-px ${
                tab === k ? "border-[#1D2951] text-[#1D2951]" : "border-transparent text-[#6B7280] hover:text-[#374151]"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "calibration" && calibrationTotal > 0 && (
          <CalibrationTab initialItem={itemFor("calibration")} onItemChange={onItemChange} toast={toast} />
        )}
        {tab === "echo" && <EchoTab initialItem={itemFor("echo")} onItemChange={onItemChange} toast={toast} />}
        {tab === "admin" && access.admin && <AdminTab toast={toast} />}
      </>
    );
  }

  return (
    <SideNavLayout>
      <div className="px-4 sm:px-6 py-5 bg-[#FAFAF9] min-h-full">
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-[#18181B]">Interpretation Review</h1>
          <p className="text-[12px] text-[#6B7280]">
            Blinded calibration of ultrasound reads and review of AI echo drafts. Study tool — nothing here is filed to the chart.
          </p>
        </div>
        {body}
      </div>
    </SideNavLayout>
  );
};

export default InterpReview;
